import { useCallback, useState, useSyncExternalStore, type ReactNode } from 'react';
import type { CanvasView } from '../../types';
import { useBoards } from '../../api/hooks';
import {
  DEFAULT_WORKSPACE_SHELL,
  effectiveBoardsInUse,
  useWorkspaceShellStore,
} from '../../store/workspaceShellStore';
import { CanvasViewTabs, type BoardTabItem } from './CanvasViewTabs';
import { ChatPanel } from './ChatPanel';
import { ProvenanceBanner } from './ProvenanceBanner';
import { CanvasOverlayRootContext } from './canvasOverlayRoot';
import { ViewLibrary } from './ViewLibrary';

interface WorkspaceShellProps {
  activeView: CanvasView;
  /** The catalog's entity term, capitalized — the entities section's label. */
  entityLabel: string;
  /** The catalog's humanized vertical — the library's Templates shelf heading. */
  verticalLabel: string | null;
  /** The active catalog id — the permanent `ChatPanel`'s conversation thread key. */
  catalogId: string;
  onNavigate: (view: CanvasView) => void;
  /** The active view's agent-nav provenance (`workspaceStore.provenance`), or `null`.
   *  When `source: 'agent'`, the frame ProvenanceBanner renders above the canvas. */
  provenance: { source: 'user' | 'agent'; narration: string } | null;
  /** Dismiss the provenance banner (→ `workspaceStore.clearProvenance`). */
  onClearProvenance: () => void;
  /** The active view body (the router `<Outlet>`). */
  children: ReactNode;
}

/**
 * Canvas P2 (SC-2655) → P3 (SC-2659) → SC-2669 — the workspace frame:
 * `canvas ∣ ChatPanel`, where the canvas column stacks the section tabs
 * (`CanvasViewTabs`) over the active view. A full-height flex row where each column
 * scrolls independently (the canvas content is its own scroll container); the chat
 * is a permanent in-flow panel (P3). The app-level icon rail (SidebarNav) sits to
 * the left of this frame. The sole catalog workspace since the SC-2671 cutover.
 */
export function WorkspaceShell({
  activeView,
  entityLabel,
  verticalLabel,
  catalogId,
  onNavigate,
  provenance,
  onClearProvenance,
  children,
}: WorkspaceShellProps) {
  // The tab bar is a SELECTION from the View Library (SC-2873): which core
  // views + boards are on the bar comes from the per-catalog workspace shell;
  // the library (the `+`) is where that selection is managed.
  const shell = useWorkspaceShellStore((s) => s.byCatalog[catalogId] ?? DEFAULT_WORKSPACE_SHELL);
  const boardsQuery = useBoards(catalogId);
  const [libraryOpen, setLibraryOpen] = useState(false);
  // Stable identity: the library's Esc/focus effect keys on its close handler.
  const closeLibrary = useCallback(() => setLibraryOpen(false), []);
  // EFFECTIVE pins (required org boards merge in ahead of personal pins — the
  // one shared predicate) resolve to tab items through the library list; a
  // pinned id whose board is gone (deleted elsewhere / still loading) simply
  // isn't shown.
  const boardTabs: BoardTabItem[] = effectiveBoardsInUse(shell, boardsQuery.data ?? []).flatMap(
    (id) => {
      const board = boardsQuery.data?.find((b) => b.id === id);
      return board ? [{ id, title: board.title }] : [];
    },
  );
  // The column-scoped mount for `CanvasOverlay` (P3b, SC-2665). A detail overlay
  // portals into this layer so it covers the content column only — the section tabs
  // and the ChatPanel stay on screen and interactive. State (not a plain ref) so the
  // consumer re-renders once the node is attached; `setOverlayRoot` is a stable
  // state setter, so passing it as the ref callback runs once on mount/unmount.
  const [overlayRoot, setOverlayRoot] = useState<HTMLElement | null>(null);
  // Whether a detail overlay is mounted over this catalog's canvas column. When one
  // is, the view content behind the scrim goes `inert` (SC-2671 a11y) so keyboard
  // focus can't reach the dimmed table/controls underneath — the shared overlay is
  // deliberately NON-modal with no focus trap, so this is what keeps focus out. The
  // section tabs + ChatPanel sit OUTSIDE this subtree, so they stay reachable.
  //
  // Driven off whether the overlay root actually HAS content, not a store flag: tabs
  // that open a detail via local component state (Findings, Attributes) never touch
  // `workspaceStore.overlay`, so a flag-based gate silently skipped inert for them
  // once their focus traps were removed (SC-2752 / Greptile). Observing the root's
  // children covers every overlay — store-driven, local-state, or agent — by
  // construction, which is the point of routing them all through one column root.
  // Subscribe to the root's child list via `useSyncExternalStore`: `getSnapshot`
  // reads the current presence (so an overlay already mounted on first paint counts),
  // and the MutationObserver re-reads it on every open/close. Keyed on `overlayRoot`
  // so it re-subscribes once the ref-callback attaches the node.
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (!overlayRoot) return () => {};
      const observer = new MutationObserver(onChange);
      observer.observe(overlayRoot, { childList: true });
      return () => observer.disconnect();
    },
    [overlayRoot],
  );
  const hasOverlay = useSyncExternalStore(
    subscribe,
    () => (overlayRoot ? overlayRoot.childElementCount > 0 : false),
  );
  return (
    <div style={{ height: '100%', minHeight: 0 }}>
      {/* The whole workspace goes `inert` while the View Library dialog covers it
          (SC-2873 Greptile P1): the library is a full-viewport scrim, so unlike the
          column-scoped detail overlays (which deliberately keep tabs + chat live),
          NOTHING behind it should stay keyboard-reachable. Same presence-driven
          inert discipline as the column overlay above, one level up. */}
      <div inert={libraryOpen} style={{ display: 'flex', height: '100%', minHeight: 0 }}>
      {/* Canvas SIDE — section tabs stacked over the active view. */}
      <div style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Section tabs — atop the canvas and OUTSIDE the overlay's containing block
            below, so they stay visible + interactive while a detail overlay is open
            (the overlay is scoped to the content column only). */}
        <div style={{ flexShrink: 0, padding: '20px 32px 0' }}>
          <CanvasViewTabs
            activeView={activeView}
            entityLabel={entityLabel}
            coreViewsInUse={shell.coreViewsInUse}
            boards={boardTabs}
            onNavigate={onNavigate}
            onOpenLibrary={() => setLibraryOpen(true)}
          />
        </div>
        {/* Content COLUMN — a non-scrolling `position: relative` wrapper so the
            overlay root below pins to the VISIBLE column (an absolute child of the
            scroll area would scroll away with the content). */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* The active view; owns its scroll + padding (edge-to-edge with the chat,
              which sits flush to the right). The frame-level ProvenanceBanner (P4,
              SC-2663) sits above the view when an agent navigation drove it — one
              banner for the whole canvas, not per-view (the entities view uses its own
              table caption instead, so it never records frame provenance). */}
          <div
            inert={hasOverlay}
            style={{ flex: 1, minWidth: 0, overflow: 'auto', padding: '16px 32px 32px' }}
          >
            {provenance?.source === 'agent' && (
              <ProvenanceBanner narration={provenance.narration} onClear={onClearProvenance} />
            )}
            <CanvasOverlayRootContext.Provider value={overlayRoot}>
              {children}
            </CanvasOverlayRootContext.Provider>
          </div>
          {/* Column overlay root (P3b): `CanvasOverlay` portals detail overlays here.
              `pointerEvents: none` so an EMPTY root lets clicks fall through to the
              view; the overlay's own scrim re-enables pointer events when open. */}
          <div ref={setOverlayRoot} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} />
        </div>
      </div>
      <ChatPanel catalogId={catalogId} />
      </div>
      <ViewLibrary
        open={libraryOpen}
        onClose={closeLibrary}
        catalogId={catalogId}
        entityLabel={entityLabel}
        verticalLabel={verticalLabel}
        activeView={activeView}
      />
    </div>
  );
}
